import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import history from '../../../../../ud-ui/components/fabric-canvas/addons/history';
import { GridLoader } from 'react-spinners';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pages } from 'modules/navigation/domain/enums/pages';
import Canvas from './canvas';
import { Button, Container, Header, MainBlock, RightBlock, TemplateName } from './styles';
import useLocalStorage from '../../../hooks/use-local-storage';
import { Zoom } from './zoom';
import useTemplates from '../../../hooks/use-templates';
import useBoards from '../../../hooks/use-boards';
import { selectFabricManager, selectHasChange, selectIsInit, selectSavedScheme } from '../../../../store/editor/selectors';
import { Scheme } from 'ud-ui/components/fabric-canvas/types';
import _ from 'lodash';
import { AppDispatch } from 'store/store';
import { setHasChanges, setPreviewState } from '../../../../store/editor/editor';
import useSaveScheme from '../../../hooks/use-save-scheme';
import { toast } from 'react-toastify';

function Content() {
  const state = useLocation().state as null | { from: string };
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { activeTemplate } = useTemplates();
  const { activeBoard, activeScheme, isProcessed } = useBoards();
  const hasChange = useSelector(selectHasChange);
  const savedScheme = useSelector(selectSavedScheme);
  const isInit = useSelector(selectIsInit);
  const manager = useSelector(selectFabricManager);
  const { setData, getData } = useLocalStorage();
  const { saveScheme } = useSaveScheme();
  const boardName = useMemo(() => {
    return activeBoard?.name || getData()?.boardName;
  }, [activeBoard?.name, getData]);

  setData({
    scheme: activeScheme,
    boardId: activeBoard?.id,
    resolution: activeTemplate?.resolution,
    boardType: activeBoard?.type,
    from: state?.from,
    templateId: activeTemplate?.id,
    boardName: activeBoard?.name,
  });

  useEffect(() => {
    const historySubscriber = {
      update: (state: Scheme) => {
        if (!manager) return;
        if (!savedScheme) return;
        const isEqual = _.isEqual(state, savedScheme);
        dispatch(setHasChanges(!isEqual));
      },
    };

    history.onChangeState.subscribe(historySubscriber);

    return () => {
      history.onChangeState.unsubscribe(historySubscriber);
    };
  }, [dispatch, manager, savedScheme]);

  const onClickSave = useCallback(() => {
    saveScheme()
      .then(() => {
        toast.success('Табло успешно сохранено');
      })
      .catch((e) => {
        console.error(e);
        toast.error('Не удалось сохранить');
      });
  }, [saveScheme]);
  const onGoPreview = useCallback(() => {
    if (!manager) {
      console.warn({ event: 'go to preview', message: 'manager not found' });
      return;
    }

    if (!activeTemplate) {
      console.warn({ event: 'go to preview', message: 'template not found', activeTemplate });
      return;
    }

    if (!activeBoard) {
      console.warn({ event: 'go to preview', message: 'board not found', activeBoard });
      return;
    }

    history.disableHistory();
    manager.canvas.discardActiveObject();
    const historyState = history.getHistory();
    const curScheme = manager.getScheme(true);
    dispatch(
      setPreviewState({
        curHistoryIndex: historyState.index,
        history: historyState.history,
        scheme: curScheme,
        resolution: manager.resolution.value,
        backgroundColor: manager.canvas.backgroundColor?.toString(),
        viewport: manager.canvas.viewportTransform ? [...manager.canvas.viewportTransform] : undefined,
      }),
    );

    navigate(Pages.TEMPLATE.builders.preview(activeTemplate.id, activeBoard.id));
  }, [activeBoard, activeTemplate, dispatch, manager, navigate]);

  return (
    <MainBlock className={'flex-center flex-col h-full w-full'}>
      {!isInit && <GridLoader style={{ position: 'absolute' }} />}
      <div style={{ visibility: isInit ? 'visible' : 'hidden' }}>
        <Header>
          <div>
            <TemplateName>{boardName ?? 'Неизвестное табло'}</TemplateName>
          </div>
          <RightBlock>
            <Button onClick={onGoPreview} color={'#FFF'}>
              Предпросмотр
            </Button>
            <Button disabled={!hasChange} onClick={onClickSave} color={'#D9B244'}>
              {isProcessed ? 'Сохранение' : hasChange ? 'Сохранить' : 'Сохранено'}
            </Button>
          </RightBlock>
        </Header>
        <Container>
          <Canvas />
          <Zoom />
        </Container>
      </div>
    </MainBlock>
  );
}

export default Content;
